/**
 * Routes List — displays the user's Routes.
 *
 * Rendered inside the Logbook's "Routes" tab. Shows routes from
 * routeStorage that the user owns (imported, derived, and auto-created
 * by plans). Each card shows related Plan/Activity counts from the
 * edge graph. Actions: "Start a Plan", "Delete".
 */

import { logger } from '../../utils/logger.js'
import { LitElement, html, css, nothing } from 'lit'
import { customElement, state } from 'lit/decorators.js'
import { routeStorage } from '../../storage/route-storage.js'
import { countsByObject } from '../../storage/relationship-counts.js'
import { router } from '../../utils/router.js'
import type { Route } from '../../schemas/route.js'

import '@material/web/button/filled-button.js'
import '@material/web/button/text-button.js'
import '@material/web/iconbutton/icon-button.js'
import '@material/web/progress/linear-progress.js'
import '../common/confirm-dialog'
import type { ConfirmDialog } from '../common/confirm-dialog.js'

import { authService } from '../../services/auth/auth-service.js'
import type { AuthState } from '../../services/auth/auth-context.js'
import { syncListOnNavigate } from '../../storage/sync-manager.js'

const log = logger.create('routes-list')

@customElement('routes-list')
export class RoutesList extends LitElement {
  static override styles = css`
    :host {
      display: flex;
      flex-direction: column;
      height: 100%;
      overflow: hidden;
    }

    .sync-bar {
      --md-linear-progress-active-indicator-color: var(--cairn-primary-color, #3b82f6);
      --md-linear-progress-track-color: transparent;
      --md-linear-progress-track-height: 2px;
      --md-linear-progress-active-indicator-height: 2px;
      width: 100%;
      flex-shrink: 0;
    }

    .list-content {
      flex: 1;
      padding: var(--cairn-spacing-md, 16px);
      overflow-y: auto;
      min-height: 0;
      transition: transform 0.15s ease-out;
    }

    .controls {
      display: flex;
      gap: var(--cairn-spacing-sm, 8px);
      align-items: center;
      margin-bottom: var(--cairn-spacing-md, 16px);
    }

    .search-input {
      flex: 1;
      padding: 10px 12px;
      border: 1px solid var(--cairn-border-color, #e2e8f0);
      border-radius: var(--cairn-border-radius, 8px);
      font-size: var(--cairn-text-size-sm, 14px);
      background: var(--cairn-surface-color, #ffffff);
      color: var(--cairn-text-color, #1e293b);
      outline: none;
      transition: border-color var(--cairn-animation-duration, 0.2s);
    }

    .search-input:focus {
      border-color: var(--cairn-primary-color, #3b82f6);
    }

    .search-input::placeholder {
      color: var(--cairn-text-secondary-color, #64748b);
    }

    .route-grid {
      display: grid;
      gap: 16px;
      grid-template-columns: 1fr;
    }

    @media (min-width: 901px) and (max-width: 1300px) {
      .route-grid {
        grid-template-columns: 1fr 1fr;
      }
    }

    @media (min-width: 1301px) {
      .route-grid {
        grid-template-columns: 1fr 1fr 1fr;
      }
    }

    .route-card {
      background: var(--cairn-surface-color, #ffffff);
      border: 1px solid var(--cairn-border-color, #e2e8f0);
      border-radius: var(--cairn-border-radius, 8px);
      padding: var(--cairn-spacing-md, 16px);
      cursor: pointer;
      transition: all var(--cairn-animation-duration, 0.2s);
      display: flex;
      flex-direction: column;
      gap: var(--cairn-spacing-sm, 8px);
      box-sizing: border-box;
    }

    .route-card:hover {
      border-color: var(--cairn-primary-color, #3b82f6);
      box-shadow: var(--cairn-shadow, 0 2px 4px rgba(0, 0, 0, 0.1));
    }

    .card-header {
      display: flex;
      align-items: flex-start;
      justify-content: space-between;
      gap: var(--cairn-spacing-sm, 8px);
    }

    .card-title {
      flex: 1;
    }

    .route-name {
      font-size: var(--cairn-text-size-lg, 18px);
      font-weight: var(--cairn-font-weight-bold, 600);
      color: var(--cairn-text-color, #1e293b);
      margin: 0;
    }

    .difficulty-badge {
      display: inline-flex;
      align-items: center;
      gap: 4px;
      font-size: 12px;
      font-weight: 500;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      padding: 2px 8px;
      border-radius: 12px;
      margin-top: 4px;
    }

    .difficulty-badge.beginner {
      background: #dcfce7;
      color: #166534;
    }
    .difficulty-badge.intermediate {
      background: #dbeafe;
      color: #1e40af;
    }
    .difficulty-badge.advanced {
      background: #fef3c7;
      color: #92400e;
    }
    .difficulty-badge.expert {
      background: #fecaca;
      color: #991b1b;
    }

    .tags {
      display: flex;
      flex-wrap: wrap;
      gap: 4px;
    }

    .tag {
      font-size: 12px;
      padding: 2px 8px;
      border-radius: 12px;
      background: var(--cairn-surface-hover-color, #f1f5f9);
      color: var(--cairn-text-secondary-color, #64748b);
    }

    .description {
      font-size: var(--cairn-text-size-sm, 14px);
      color: var(--cairn-text-secondary-color, #64748b);
      line-height: 1.4;
      overflow: hidden;
      display: -webkit-box;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
      text-overflow: ellipsis;
    }

    .related-counts {
      display: flex;
      flex-wrap: wrap;
      gap: 8px;
    }

    .count-chip {
      display: inline-flex;
      align-items: center;
      gap: 4px;
      font-size: 12px;
      padding: 2px 8px;
      border-radius: 12px;
      background: var(--cairn-surface-hover-color, #f1f5f9);
      color: var(--cairn-text-secondary-color, #64748b);
    }

    .count-chip .material-icons {
      font-size: 14px;
    }

    .card-actions-row {
      display: flex;
      gap: var(--cairn-spacing-sm, 8px);
      margin-top: auto;
      padding-top: var(--cairn-spacing-sm, 8px);
    }

    .empty-state {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      text-align: center;
      padding: var(--cairn-spacing-xl, 32px);
      min-height: 300px;
    }

    .empty-icon {
      font-family: 'Material Symbols Outlined';
      font-size: 48px;
      margin-bottom: var(--cairn-spacing-md, 16px);
      color: var(--cairn-text-secondary-color, #64748b);
      opacity: 0.6;
    }

    .empty-title {
      font-size: var(--cairn-text-size-lg, 20px);
      font-weight: var(--cairn-font-weight-bold, 600);
      color: var(--cairn-text-color, #1e293b);
      margin: 0 0 var(--cairn-spacing-sm, 8px) 0;
    }

    .empty-description {
      font-size: var(--cairn-text-size-md, 16px);
      color: var(--cairn-text-secondary-color, #64748b);
      margin-bottom: var(--cairn-spacing-lg, 24px);
      max-width: 400px;
    }

    .material-icons {
      font-family: 'Material Symbols Outlined';
      font-weight: normal;
      font-style: normal;
      line-height: 1;
      letter-spacing: normal;
      text-transform: none;
      display: inline-block;
      white-space: nowrap;
      word-wrap: normal;
      direction: ltr;
      user-select: none;
    }
  `

  @state() private routes: Route[] = []
  @state() private loaded = false
  @state() private syncing = false
  @state() private searchQuery = ''
  @state() private activityCountByRoute: Map<string, number> = new Map()

  private authState: AuthState = authService.getState()
  private unsubAuth?: () => void

  override connectedCallback(): void {
    super.connectedCallback()
    this.authState = authService.getState()
    this.unsubAuth = authService.onChange(state => {
      this.authState = state
      void this.loadRoutes()
    })
    void this.loadRoutes()
    this.syncing = true
    void syncListOnNavigate('routes')
      ?.then(() => this.loadRoutes())
      .finally(() => {
        this.syncing = false
      })
    document.addEventListener('visibilitychange', this.handleVisibilityChange)
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback()
    this.unsubAuth?.()
    document.removeEventListener('visibilitychange', this.handleVisibilityChange)
  }

  private handleVisibilityChange = (): void => {
    if (!document.hidden) {
      void this.loadRoutes()
    }
  }

  private async loadRoutes(): Promise<void> {
    try {
      const result = await routeStorage.getRoutesForUser(this.authState.userId)
      if (result.success && result.data) {
        this.routes = result.data
      }
      await this.loadCounts()
    } catch (error) {
      log.error('Failed to load routes:', error)
    } finally {
      this.loaded = true
    }
  }

  private async loadCounts(): Promise<void> {
    try {
      this.activityCountByRoute = await countsByObject('follows-route', 'route')
    } catch (error) {
      log.error('Failed to load related counts:', error)
    }
  }

  private get filteredRoutes(): Route[] {
    if (!this.searchQuery.trim()) return this.routes
    const q = this.searchQuery.toLowerCase().trim()
    return this.routes.filter(
      r =>
        (r.name.toLowerCase().includes(q) || r.description?.toLowerCase().includes(q)) ??
        r.difficulty?.toLowerCase().includes(q) ??
        r.tags.some(t => t.toLowerCase().includes(q))
    )
  }

  private handleSearchChange(e: Event): void {
    this.searchQuery = (e.target as HTMLInputElement).value
  }

  private handleViewRoute(route: Route): void {
    router.navigate(`/routes/${route.id}`)
  }

  private handleStartPlan(e: Event, route: Route): void {
    e.stopPropagation()
    router.navigate(`/plans/new?routeId=${route.id}`)
  }

  private async handleDeleteRoute(route: Route): Promise<void> {
    const dialog = this.shadowRoot?.querySelector('confirm-dialog') as ConfirmDialog | undefined
    if (dialog) {
      dialog.headline = 'Delete Route'
      dialog.message = `Delete "${route.name}"? This cannot be undone.`
      const confirmed = await dialog.show()
      if (!confirmed) return
    }
    try {
      await routeStorage.deleteRoute(route.id)
      await this.loadRoutes()
    } catch (error) {
      log.error('Failed to delete route:', error)
    }
  }

  // --- Pull-to-refresh ---
  private pullStartY = 0
  private pulling = false
  private scrollerEl: HTMLElement | null = null

  private handleTouchStart(e: TouchEvent): void {
    const scroller = e.currentTarget as HTMLElement
    this.scrollerEl = scroller
    if (scroller.scrollTop <= 0) {
      this.pullStartY = e.touches[0].clientY
      this.pulling = true
    }
  }

  private handleTouchMove(e: TouchEvent): void {
    if (!this.pulling || !this.scrollerEl) return
    const dy = e.touches[0].clientY - this.pullStartY
    if (dy > 0) {
      const clamped = Math.min(dy * 0.4, 80)
      this.scrollerEl.style.transform = `translateY(${clamped}px)`
    } else {
      this.pulling = false
      this.scrollerEl.style.transform = ''
    }
  }

  private handleTouchEnd(e: TouchEvent): void {
    if (!this.pulling) return
    const dy = e.changedTouches[0].clientY - this.pullStartY
    this.pulling = false
    if (this.scrollerEl) this.scrollerEl.style.transform = ''
    if (dy > 60) {
      void this.refresh()
    }
  }

  private async refresh(): Promise<void> {
    this.syncing = true
    try {
      await syncListOnNavigate('routes')
      await this.loadRoutes()
    } finally {
      this.syncing = false
    }
  }

  private renderCounts(activityCount: number): unknown {
    if (activityCount === 0) return ''
    return html`
      <div class="related-counts">
        <span class="count-chip">
          <span class="material-icons">directions_walk</span>
          ${activityCount} ${activityCount === 1 ? 'Activity' : 'Activities'}
        </span>
      </div>
    `
  }

  private renderRouteCard(route: Route): unknown {
    const activityCount = this.activityCountByRoute.get(route.id) ?? 0
    return html`
      <div class="route-card" @click=${() => this.handleViewRoute(route)}>
        <div class="card-header">
          <div class="card-title">
            <h3 class="route-name">${route.name}</h3>
            ${route.difficulty
              ? html`<span class="difficulty-badge ${route.difficulty}">${route.difficulty}</span>`
              : ''}
          </div>
          <div @click=${(e: Event) => e.stopPropagation()}>
            <md-icon-button @click=${() => this.handleDeleteRoute(route)} aria-label="Delete route">
              <span class="material-icons">delete</span>
            </md-icon-button>
          </div>
        </div>

        ${route.tags.length > 0
          ? html`
              <div class="tags">
                ${route.tags.map(tag => html`<span class="tag">${tag}</span>`)}
              </div>
            `
          : ''}
        ${route.description ? html`<div class="description">${route.description}</div>` : ''}
        ${this.renderCounts(activityCount)}

        <div class="card-actions-row" @click=${(e: Event) => e.stopPropagation()}>
          <md-text-button @click=${(e: Event) => this.handleStartPlan(e, route)}>
            <span class="material-icons" slot="icon">map</span>
            Start a Plan
          </md-text-button>
        </div>
      </div>
    `
  }

  override render(): unknown {
    return html`
      ${this.syncing
        ? html`<md-linear-progress indeterminate class="sync-bar"></md-linear-progress>`
        : nothing}
      <div
        class="list-content"
        @touchstart=${this.handleTouchStart}
        @touchmove=${this.handleTouchMove}
        @touchend=${this.handleTouchEnd}
      >
        ${this.loaded && this.routes.length === 0
          ? html`
              <div class="empty-state">
                <div class="empty-icon">alt_route</div>
                <h2 class="empty-title">No routes yet</h2>
                <p class="empty-description">
                  Import a GPX file or save a route from an activity to see it here.
                </p>
              </div>
            `
          : html`
              <div class="controls">
                <input
                  class="search-input"
                  type="text"
                  placeholder="Search routes..."
                  .value=${this.searchQuery}
                  @input=${this.handleSearchChange}
                />
              </div>

              ${this.filteredRoutes.length === 0
                ? html`
                    <div class="empty-state">
                      <div class="empty-icon">search_off</div>
                      <h2 class="empty-title">No routes found</h2>
                      <p class="empty-description">Try adjusting your search.</p>
                    </div>
                  `
                : html`
                    <div class="route-grid">
                      ${this.filteredRoutes.map(r => this.renderRouteCard(r))}
                    </div>
                  `}
            `}
      </div>
      <confirm-dialog></confirm-dialog>
    `
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'routes-list': RoutesList
  }
}
